import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  DestroyRef,
  ElementRef,
  Input,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  WritableSignal,
  inject,
  signal,
} from '@angular/core';
import { UserInfo } from '@app-types/user-info';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { SpacerComponent } from 'src/app/common/components/spacer/spacer.component';
import { SdModelDirective } from 'src/app/common/directives/sd-model.directive';
import { Dialog } from 'src/app/common/util/dialog';
import { UserService } from 'src/openapi-client';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { AbstractControl } from '@angular/forms';
import { Validators } from '@angular/forms';
import { SuffixDirective } from '@directives/suffix.directive';
import type SdDialog from '@solid-design-system/components/dist-versioned/components/dialog/dialog';
import type SdInput from '@solid-design-system/components/dist-versioned/components/input/input';
import { NotificationUtils } from 'src/app/common/util/notification';
import { CombinedRequiredInputChecker } from 'src/app/util/combined-validation-check';
import { RelevantUserInfo } from 'src/app/util/user-info-input-fields';
import { UserInfoInputConfig } from 'src/app/util/user-info-input-groupt';

const mailKey: keyof UserInfo = 'mailAddress';

@Component({
  selector: 'wct-edit-info-dialog',
  standalone: true,
  imports: [TranslocoModule, SdModelDirective, CommonModule, SpacerComponent, SuffixDirective],
  templateUrl: './edit-info-dialog.component.html',
  styleUrl: './edit-info-dialog.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class EditInfoDialogComponent extends Dialog implements OnInit, AfterViewInit {
  @Input({ required: true }) public inputConfig!: UserInfoInputConfig;
  @Input({ required: true }) public userInfo!: UserInfo;
  @Input({ required: true }) public saveAction!: (modifiedUserInfo: UserInfo) => Promise<void>;

  @Input({ required: false }) public titleI18n: string = 'profile.account.editDialogTitle';
  @Input({ required: false }) public saveI18n: string = 'profile.account.save';
  @Input({ required: false }) public successI18n?: string;


  @ViewChild('dialog', { static: true }) protected dialog!: ElementRef<SdDialog>;
  @ViewChildren('input') protected inputElements!: QueryList<ElementRef<SdInput>>;
  @ViewChildren('input', { read: SdModelDirective }) protected inputModels!: QueryList<SdModelDirective<SdInput>>;

  protected state: RelevantUserInfo = {} as RelevantUserInfo;

  protected eMailDomain: string = '';

  protected readonly loading: WritableSignal<boolean> = signal(false);

  private readonly transloco = inject(TranslocoService);
  private readonly service = inject(UserService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly eMailError = inject(TranslocoService).translate('profile.general.emailError');

  private readonly combinedRequiredInputChecker = new CombinedRequiredInputChecker(inject(TranslocoService));

  public ngOnInit(): void {
    for (const inputGroup of this.inputConfig.inputGroups) {
      for (const input of inputGroup.inputs) {
        const key: keyof RelevantUserInfo = input.key;
        const value: string | boolean | undefined = this.userInfo[key];

        if (typeof value !== 'string') {
          continue;
        }

        if (key === 'mailAddress') {
          const mail: string[] = value.split('@');
          if (mail.length === 2) {
            this.state[key] = mail[0];
            this.eMailDomain = mail[1];
            continue;
          }
        }

        this.state[key] = value;
      }
    }

    this.state.clientId = this.userInfo.clientId;
  }

  public ngAfterViewInit(): void {
    for (const model of this.inputModels) {
      const key: string | null = model.elememtRef.nativeElement.getAttribute('key');

      if (key === null) {
        continue;
      }

      if (key === mailKey) {
        const mailAddressInputElement: SdInput = model.elememtRef.nativeElement;

        model.update.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
          const email: string = this.eMailDomain.length > 0 ? `${value}@${this.eMailDomain}` : value;
          const isValidMail: boolean = !Validators.email({ value: email } as AbstractControl)?.['email'];
          mailAddressInputElement.setCustomValidity(!isValidMail ? this.eMailError : '');
        });

        continue;
      }

      const matchingCombinedRequiredInputs =  this.inputConfig.combinedRequiredInputs.filter(config => config.keys.includes(key as keyof UserInfo));

      for (const matchingCombinedRequiredInput of matchingCombinedRequiredInputs) {
        model.update.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          this.combinedRequiredInputChecker.checkCombinedInputValidity(this.state, this.inputElements, [matchingCombinedRequiredInput]);
        })
      }
    }
  }

  protected async save() {
    if (this.loading()) {
      return;
    }

    if (!this.checkValidity()) {
      return;
    }

    this.loading.set(true);

    try {
      let state: RelevantUserInfo = this.state;

      if (this.eMailDomain.length > 0) {
        state = {
          ...state,
          mailAddress: this.eMailDomain.length > 0 ? `${this.state.mailAddress}@${this.eMailDomain}` : this.state.mailAddress,
        };
      }

      await this.saveAction(state);

      if (this.successI18n) {
        NotificationUtils.displayNotification(this.transloco.translate(this.successI18n), 'success', { closable: true, durationInMs: 10_000 });
      }

      await this.dialog.nativeElement.hide();
    } finally {
      this.loading.set(false);
    }
  }

  protected checkValidity(): boolean {
    let valid: boolean = true;

    for (const inputElement of this.inputElements) {
      valid = inputElement.nativeElement.reportValidity() && valid;
    }

    valid = this.combinedRequiredInputChecker.checkCombinedInputValidity(this.state, this.inputElements, this.inputConfig.combinedRequiredInputs) && valid;

    return valid;
  }
}
